/**
 * CreateGroupChat — picks participants for a new group conversation, mirroring
 * mobile's CreateGroupChat screen (optional name, searchable following list,
 * MIN/MAX member rules). A member passed in router state is pre-selected and
 * locked, which is how "Create group chat with X" arrives from chat details.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useMessages } from '@wlb/shared/context/MessageContext';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { MAX_GROUP_CHAT_MEMBERS, MIN_GROUP_CHAT_MEMBERS } from '@wlb/shared/constants';
import type { ConversationMember } from '@wlb/shared/types/chat';
import { CheckIcon, ChevronLeftIcon, CloseIcon, SearchIcon } from '../components/ui/icons';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import { ListStatus } from '../components/ui/ListStatus';
import { ConversationSkeleton } from '../features/messages/ConversationSkeleton';
import { resolveAvatarSrc } from '../features/messages/avatarSource';
import { getUsernameDisplay } from '../utils/usernameDisplay';
import '../features/messages/messages.css';

interface CreateGroupChatState {
  initialMember?: ConversationMember;
}

const memberLabel = (member: ConversationMember): string =>
  getUsernameDisplay(member);

export function CreateGroupChat() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useProfile();
  const { createGroupConversation, fetchMemberCandidates } = useMessages();

  const initialMember = (location.state as CreateGroupChatState | null)?.initialMember;

  const lockedIds = useMemo(
    () => new Set(initialMember?.id ? [String(initialMember.id)] : []),
    [initialMember?.id],
  );

  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selected, setSelected] = useState<ConversationMember[]>(
    initialMember?.id ? [{ ...initialMember, id: String(initialMember.id), locked: true }] : [],
  );
  const [candidates, setCandidates] = useState<ConversationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [creating, setCreating] = useState(false);
  // Guards against out-of-order resolution: only the latest debounced search
  // may write candidates/loading/error, so a slow earlier request can't
  // overwrite a newer result.
  const searchRequestIdRef = useRef(0);

  const selectedIds = useMemo(() => new Set(selected.map(member => member.id)), [selected]);

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadCandidates = useCallback(
    (query: string) => {
      const requestId = searchRequestIdRef.current + 1;
      searchRequestIdRef.current = requestId;
      setLoading(true);
      setLoadError('');
      fetchMemberCandidates(query)
        .then(users => {
          if (searchRequestIdRef.current === requestId) setCandidates(users);
        })
        .catch(caught => {
          if (searchRequestIdRef.current !== requestId) return;
          setCandidates([]);
          setLoadError(
            caught instanceof Error && caught.message
              ? caught.message
              : t('createGroupChat.membersLoadFailed'),
          );
        })
        .finally(() => {
          if (searchRequestIdRef.current === requestId) setLoading(false);
        });
    },
    [fetchMemberCandidates, t],
  );

  useEffect(() => {
    loadCandidates(appliedSearch);
  }, [appliedSearch, loadCandidates]);

  const toggleMember = useCallback(
    (member: ConversationMember) => {
      setValidationError('');
      setSelected(current => {
        if (current.some(existing => existing.id === member.id)) {
          if (lockedIds.has(member.id)) return current;
          return current.filter(existing => existing.id !== member.id);
        }
        if (current.length >= MAX_GROUP_CHAT_MEMBERS) {
          setValidationError(
            t('createGroupChat.validationMaxMembers', { count: MAX_GROUP_CHAT_MEMBERS }),
          );
          return current;
        }
        return [...current, member];
      });
    },
    [lockedIds, t],
  );

  const handleCreate = useCallback(async () => {
    if (creating) return;
    if (selected.length < MIN_GROUP_CHAT_MEMBERS) {
      setValidationError(
        t('createGroupChat.validationMinMembers', { count: MIN_GROUP_CHAT_MEMBERS }),
      );
      return;
    }
    setCreating(true);
    try {
      const conversation = await createGroupConversation({
        groupName: groupName.trim(),
        members: selected,
        createdBy: profile?.userId != null ? String(profile.userId) : null,
      });
      navigate(`/messages/${conversation.id}`, { replace: true });
    } catch (caught) {
      ToastService.show({
        type: 'error',
        message:
          caught instanceof Error && caught.message
            ? caught.message
            : t('createGroupChat.createFailed'),
      });
    } finally {
      setCreating(false);
    }
  }, [creating, selected, groupName, createGroupConversation, profile?.userId, navigate, t]);

  return (
    <section className="create-group-chat" aria-label={t('createGroupChat.title')}>
      <div className="chat-details__topbar">
        <button
          type="button"
          className="chat-header__back"
          onClick={() => navigate(-1)}
          aria-label={t('home.notifications.accessibility.back')}
        >
          <ChevronLeftIcon size={24} />
        </button>
        <h1 className="create-group-chat__title">{t('createGroupChat.title')}</h1>
      </div>

      <label className="create-group-chat__field">
        <span className="create-group-chat__label">
          {t('createGroupChat.groupNamePlaceholder')}
        </span>
        <input
          className="create-group-chat__input"
          type="text"
          value={groupName}
          onChange={event => setGroupName(event.target.value)}
          autoComplete="off"
        />
      </label>

      <div className="messages-search create-group-chat__search">
        <input
          className="messages-search__input"
          type="text"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={t('createGroupChat.searchFollowing')}
          aria-label={t('createGroupChat.searchFollowing')}
        />
        <SearchIcon size={24} className="messages-search__icon" />
      </div>

      {selected.length > 0 && (
        <ul className="create-group-chat__chips" aria-label={t('createGroupChat.members')}>
          {selected.map(member => (
            <li key={member.id} className="create-group-chat__chip">
              <Avatar
                src={resolveAvatarSrc(member.avatar)}
                name={memberLabel(member)}
                username={member.username}
                seed={member.profileUserGuid ?? member.id}
                size="sm"
              />
              <span className="create-group-chat__chip-name">{memberLabel(member)}</span>
              {!lockedIds.has(member.id) && (
                <button
                  type="button"
                  className="create-group-chat__chip-remove"
                  onClick={() => toggleMember(member)}
                  aria-label={t('createGroupChat.removeMember')}
                >
                  <CloseIcon size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <FormError message={validationError} />

      <h2 className="create-group-chat__section">{t('createGroupChat.following')}</h2>

      {loading ? (
        <ConversationSkeleton />
      ) : loadError ? (
        <ListStatus
          variant="error"
          layout="inline"
          message={loadError}
          actionLabel={t('chat.retry')}
          onAction={() => loadCandidates(appliedSearch)}
          busy={loading}
        />
      ) : candidates.length === 0 ? (
        <ListStatus
          variant="empty"
          layout="inline"
          title={t('createGroupChat.noMembersFound')}
        />
      ) : (
        <ul className="create-group-chat__list">
          {candidates.map(member => {
            const isSelected = selectedIds.has(member.id);
            const isLocked = lockedIds.has(member.id);
            return (
              <li key={member.id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected}
                  disabled={isLocked && isSelected}
                  className={`create-group-chat__row${isSelected ? ' create-group-chat__row--selected' : ''}`}
                  onClick={() => toggleMember(member)}
                >
                  <Avatar
                    src={resolveAvatarSrc(member.avatar)}
                    name={memberLabel(member)}
                    username={member.username}
                    seed={member.profileUserGuid ?? member.id}
                    size="lg"
                  />
                  <span className="create-group-chat__row-name">{memberLabel(member)}</span>
                  {isSelected && (
                    <span className="create-group-chat__row-check" aria-hidden>
                      <CheckIcon size={16} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="create-group-chat__footer">
        <Button
          fullWidth
          size="lg"
          disabled={creating || selected.length < MIN_GROUP_CHAT_MEMBERS}
          onClick={() => { handleCreate().catch(() => undefined); }}
        >
          {t('createGroupChat.createGroup')}
        </Button>
      </div>
    </section>
  );
}
